import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Star } from 'lucide-react';
import { TRUSTPILOT } from '../mock';

const TP_GREEN = '#00b67a';

// Trustpilot-style star row that supports a fractional last star (e.g. 4.3).
function TrustStars({ value = 5, size = 22 }) {
  const stars = [0, 1, 2, 3, 4];
  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {stars.map((i) => {
        const fill = Math.max(0, Math.min(1, value - i));
        return (
          <span
            key={i}
            className="relative inline-flex items-center justify-center rounded-[3px]"
            style={{ width: size, height: size, backgroundColor: '#dcdce6' }}
          >
            <span
              className="absolute inset-y-0 left-0 overflow-hidden rounded-l-[3px]"
              style={{ width: `${fill * 100}%`, backgroundColor: TP_GREEN }}
            />
            <Star
              size={size * 0.62}
              className="relative"
              style={{ color: '#fff', fill: '#fff' }}
              strokeWidth={0}
            />
          </span>
        );
      })}
    </div>
  );
}

function useCardsPerView() {
  const getCount = () => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 640) return 2;
    return 1;
  };
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const onResize = () => setCount(getCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return count;
}

function ReviewCard({ review }) {
  return (
    <figure className="flex h-full flex-col overflow-hidden rounded-3xl border border-[var(--ob-line)] bg-white shadow-[0_1px_0_rgba(17,26,35,0.04)]">
      <figcaption className="flex items-center justify-between gap-3 border-b border-[var(--ob-line)] px-5 py-4">
        <div className="min-w-0">
          <div className="truncate font-display text-sm font-bold text-[var(--ob-ink)]">
            {review.name}
          </div>
          <div className="truncate text-xs text-[var(--ob-ink-soft)]">{review.title}</div>
        </div>
        <TrustStars value={review.rating} size={16} />
      </figcaption>
      <div
        className="tp-scroll flex-1 overflow-y-auto bg-white p-3"
        tabIndex={0}
        aria-label={`Scrollable review from ${review.name}`}
      >
        <img
          src={review.image}
          alt={review.alt}
          loading="lazy"
          className="block h-auto w-full rounded-xl"
        />
      </div>
    </figure>
  );
}

export default function TrustpilotShowcase() {
  const { reviews, score, label, totalReviews, scoreValue, profileUrl, logo } = TRUSTPILOT;
  const perView = useCardsPerView();
  const total = reviews.length;
  const maxIndex = Math.max(0, total - perView);
  const [index, setIndex] = useState(0);
  const touch = useRef({ x: 0, active: false });

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(maxIndex, i + 1)),
    [maxIndex]
  );

  const pages = useMemo(
    () => Array.from({ length: maxIndex + 1 }, (_, i) => i),
    [maxIndex]
  );

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  };

  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, active: true };
  };
  const onTouchEnd = (e) => {
    if (!touch.current.active) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    if (Math.abs(dx) > 40) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touch.current.active = false;
  };

  const canPrev = index > 0;
  const canNext = index < maxIndex;

  return (
    <div id="reviews" className="mt-10 md:mt-12 scroll-mt-24" data-testid="trustpilot-showcase">
      {/* Overall rating (transcribed from genuine Trustpilot profile) - entire card links out */}
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="trustpilot-profile-link"
        aria-label={`OnBrand Emails rated ${score} out of 5, ${label}, based on ${totalReviews} reviews. Read our reviews on Trustpilot (opens in a new tab).`}
        className="group mb-8 flex flex-col items-start gap-5 rounded-3xl border border-[var(--ob-line)] bg-white px-6 py-6 outline-none transition-all duration-200 hover:border-[var(--ob-ink)] hover:shadow-[0_12px_30px_-16px_rgba(17,26,35,0.35)] focus-visible:ring-2 focus-visible:ring-[var(--ob-accent)] sm:flex-row sm:items-center sm:justify-between md:px-8"
      >
        <div className="flex items-center gap-5">
          <div className="flex flex-col items-center leading-none">
            <span
              className="font-display text-5xl font-extrabold text-[var(--ob-ink)]"
              data-testid="trustpilot-score"
            >
              {score}
            </span>
            <span className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ob-ink-soft)]">
              out of 5
            </span>
          </div>
          <div className="h-14 w-px bg-[var(--ob-line)]" />
          <div>
            <TrustStars value={scoreValue} size={24} />
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-display text-base font-bold text-[var(--ob-ink)]">
                {label}
              </span>
              <span className="text-sm text-[var(--ob-ink-soft)]" data-testid="trustpilot-count">
                Based on {totalReviews} reviews on
              </span>
              {logo ? (
                <img
                  src={logo}
                  alt="Trustpilot"
                  className="h-5 w-auto"
                  data-testid="trustpilot-logo"
                />
              ) : (
                <span className="text-sm font-semibold text-[var(--ob-ink-soft)]">Trustpilot</span>
              )}
            </div>
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[var(--ob-dark)] px-5 py-3 text-sm font-semibold text-white transition-transform duration-200 group-hover:scale-[1.03]">
          Read our reviews on Trustpilot
          <ExternalLink size={15} />
        </span>
      </a>

      {/* Carousel */}
      <div
        className="relative"
        role="region"
        aria-roledescription="carousel"
        aria-label="Trustpilot customer reviews"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="overflow-hidden">
          <div
            className="flex"
            style={{
              transform: `translateX(-${(index * 100) / perView}%)`,
              transition: 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {reviews.map((review) => (
              <div
                key={review.id}
                className="shrink-0 px-2.5"
                style={{ width: `${100 / perView}%`, height: 460 }}
              >
                <ReviewCard review={review} />
              </div>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={goPrev}
            disabled={!canPrev}
            aria-label="Previous reviews"
            data-testid="trustpilot-prev"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--ob-line)] bg-white text-[var(--ob-ink)] transition-all duration-200 hover:border-[var(--ob-ink)] disabled:cursor-not-allowed disabled:opacity-35"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="flex items-center gap-2" role="tablist" aria-label="Review pages">
            {pages.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setIndex(p)}
                aria-label={`Go to review set ${p + 1}`}
                aria-selected={p === index}
                data-testid={`trustpilot-dot-${p}`}
                className="h-2.5 rounded-full transition-all duration-300"
                style={{
                  width: p === index ? 26 : 10,
                  backgroundColor: p === index ? 'var(--ob-accent)' : 'var(--ob-line)',
                }}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={goNext}
            disabled={!canNext}
            aria-label="Next reviews"
            data-testid="trustpilot-next"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--ob-line)] bg-white text-[var(--ob-ink)] transition-all duration-200 hover:border-[var(--ob-ink)] disabled:cursor-not-allowed disabled:opacity-35"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-[var(--ob-ink-soft)]">
        A curated showcase of our genuine Trustpilot reviews.
      </p>
    </div>
  );
}
